/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { GroupLevelItem } from '#PRIVATE_Axis/groupAxis/hierarchicalUtils';
import type { ChartSeriesData, Info, ChartContextMenuContext } from '#utils/UNSAFE_visTypes/chart';
import { getInfo } from '#utils/UNSAFE_visUtils';
import type { RefObject } from 'preact';

export function getChartContextMenuContext<K, D>(
  focusedItemInfo: Info,
  focusedGroupInfo: Info,
  gesture: 'mouse' | 'touch' | 'keyboard',
  target: HTMLElement,
  series: ChartSeriesData<K, D>[],
  levelsArray: GroupLevelItem[][],
  rootRef: RefObject<HTMLElement>,
  xAxisTitle?: string,
  yAxisTitle?: string,
  getDataItem?: (seriesIndex: number, groupIndex: number, groupPath: number[]) => D | undefined
) {
  let seriesIndex;
  let groupIndex;
  let levelIndex;
  if (gesture === 'keyboard') {
    //We find the element where the position of the menu is going to be based of
    groupIndex = focusedItemInfo.groupIndex;
    seriesIndex = focusedItemInfo.seriesIndex;
    levelIndex = focusedGroupInfo.levelIndex;
  } else {
    //We search for the key using the target of the event
    const info = getInfo(rootRef, target);
    seriesIndex = info ? info.seriesIndex : undefined;
    groupIndex = info ? info.groupIndex : undefined;
    levelIndex = info ? info.levelIndex : undefined;
  }
  let context;
  if (seriesIndex !== undefined && groupIndex !== undefined && getDataItem) {
    const groupPath = levelsArray[levelsArray.length - 1]?.[groupIndex]?.path;
    const data = getDataItem(seriesIndex, groupIndex, groupPath);
    context = {
      data,
      groupIndex,
      seriesIndex,
      groupPath,
      type: 'item'
    };
  } else if (seriesIndex !== undefined) {
    context = {
      data: series[seriesIndex],
      type: 'series'
    };
  } else if (groupIndex !== undefined && levelIndex != undefined) {
    context = {
      data: levelsArray[levelIndex]?.[groupIndex],
      type: 'xAxisTickLabel'
    };
  } else {
    const target = event?.target as HTMLElement;
    const text = target?.textContent;
    if ((text && gesture !== 'keyboard' && text === xAxisTitle) || text === yAxisTitle) {
      context = {
        type: 'axisTitle',
        axis: text === xAxisTitle ? 'x' : 'y'
      };
    } else {
      context = {
        type: 'background'
      };
    }
  }
  return {
    context: context as ChartContextMenuContext<K, D>
  };
}
